import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
}

interface Operator {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  avatar?: string;
  department?: string;
}

interface TicketTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  tickets: Ticket[];
  onTransferComplete: () => void;
}

const TicketTransferModal: React.FC<TicketTransferModalProps> = ({
  isOpen,
  onClose,
  tickets,
  onTransferComplete
}) => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
  const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOperators();
      // Выбираем все заявки по умолчанию
      setSelectedTickets(new Set(tickets.map(t => t.id)));
    }
  }, [isOpen, tickets]);

  const fetchOperators = async () => {
    try {
      setLoadingOperators(true);
      const response = await api.get('/api/tickets/transfer-candidates');
      if (response.data.success) {
        setOperators(response.data.data || []);
      }
    } catch (error: any) {
      toast.error('Ошибка загрузки списка операторов');
    } finally {
      setLoadingOperators(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedTickets.size === tickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map(t => t.id)));
    }
  };

  const handleToggleTicket = (ticketId: string) => {
    const newSelected = new Set(selectedTickets);
    if (newSelected.has(ticketId)) {
      newSelected.delete(ticketId);
    } else {
      newSelected.add(ticketId);
    }
    setSelectedTickets(newSelected);
  };

  const handleTransfer = async (transferAll: boolean = false) => {
    if (!selectedOperatorId) {
      toast.error('Выберите оператора для передачи');
      return;
    }

    const ticketsToTransfer = transferAll 
      ? tickets.map(t => t.id)
      : Array.from(selectedTickets);

    if (ticketsToTransfer.length === 0) {
      toast.error('Выберите хотя бы одну заявку');
      return;
    }

    try {
      setLoading(true);

      if (ticketsToTransfer.length === 1) {
        // Передача одной заявки
        const response = await api.post(`/api/tickets/${ticketsToTransfer[0]}/transfer`, {
          toOperatorId: selectedOperatorId,
          reason: reason || 'Передача при закрытии смены',
          comment
        });

        if (response.data.success) {
          toast.success('Заявка успешно передана');
          onTransferComplete();
          onClose();
        }
      } else {
        // Массовая передача
        const response = await api.post('/api/tickets/bulk-transfer', {
          ticketIds: ticketsToTransfer,
          toOperatorId: selectedOperatorId,
          reason: reason || 'Массовая передача при закрытии смены',
          comment
        });

        if (response.data.success) {
          const { success, failed } = response.data.data;
          if (failed.length > 0) {
            toast.error(`Передано: ${success.length}, ошибок: ${failed.length}`);
          } else {
            toast.success(`Успешно передано заявок: ${success.length}`);
          }
          onTransferComplete();
          onClose();
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при передаче заявок');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <h3 className="text-lg font-medium text-foreground">
            Передача заявок при закрытии смены
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Список заявок */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-foreground">
                Открытые заявки ({tickets.length})
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedTickets.size === tickets.length ? 'Снять все' : 'Выбрать все'}
              </Button>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-md p-2">
              {tickets.map(ticket => (
                <label
                  key={ticket.id}
                  className="flex items-center space-x-3 p-2 hover:bg-accent rounded-md cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTickets.has(ticket.id)}
                    onChange={() => handleToggleTicket(ticket.id)}
                    className="rounded border-border"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {ticket.ticketNumber}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {ticket.subject}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Статус: {ticket.status} | Приоритет: {ticket.priority}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Выбор оператора */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-sm font-medium text-foreground">
                Передать оператору *
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fetchOperators}
                disabled={loadingOperators}
              >
                {loadingOperators ? 'Обновляем...' : 'Обновить список'}
              </Button>
            </div>
            {loadingOperators ? (
              <div className="text-sm text-muted-foreground">Загрузка...</div>
            ) : (
              <>
                <select
                  value={selectedOperatorId}
                  onChange={(e) => setSelectedOperatorId(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  required
                  disabled={operators.length === 0}
                >
                  <option value="">Выберите оператора</option>
                  {operators.map(operator => (
                    <option key={operator.id} value={operator.id}>
                      {operator.firstName} {operator.lastName} (@{operator.username})
                      {operator.department && ` - ${operator.department}`}
                    </option>
                  ))}
                </select>

                {operators.length === 0 && (
                  <div className="mt-2 text-sm text-muted-foreground">
                    Нет доступных операторов на смене (online/busy/break). Передача невозможна.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Причина передачи */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Причина передачи
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Например: Закрытие смены"
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            />
          </div>

          {/* Комментарий */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Комментарий (необязательно)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Дополнительная информация о передаче..."
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            variant="outline"
            onClick={() => handleTransfer(false)}
            disabled={loading || selectedTickets.size === 0 || !selectedOperatorId || operators.length === 0}
          >
            {loading ? 'Передача...' : `Передать выбранные (${selectedTickets.size})`}
          </Button>
          <Button
            onClick={() => handleTransfer(true)}
            disabled={loading || !selectedOperatorId || operators.length === 0}
          >
            {loading ? 'Передача...' : 'Передать все'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TicketTransferModal;

